import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useShowUser } from '@/context/ShowUserContext';
import { cn } from '@/lib/core/utils';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function HighlightOverlay() {
  const { state, dismiss } = useShowUser();
  const [rect, setRect] = useState<HighlightRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom');
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (!state.active || !state.element) {
      setRect(null);
      return;
    }

    // Find element by data-showable attribute
    const normalizedElement = state.element.toLowerCase().replace(/\s+/g, '-');
    const element = document.querySelector(`[data-showable="${normalizedElement}"]`);

    if (!element) {
      console.log('[HighlightOverlay] Element not found:', normalizedElement);
      setRect(null);
      return;
    }

    const updateRect = () => {
      const domRect = element.getBoundingClientRect();
      setRect({
        top: domRect.top + window.scrollY,
        left: domRect.left + window.scrollX,
        width: domRect.width,
        height: domRect.height,
      });

      // Determine tooltip position based on available space
      const spaceBelow = window.innerHeight - domRect.bottom;
      setTooltipPosition(spaceBelow < 100 ? 'top' : 'bottom');
    };

    // Initial position
    updateRect();

    // Scroll element into view smoothly
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Update on scroll/resize
    window.addEventListener('scroll', updateRect);
    window.addEventListener('resize', updateRect);

    // Watch for element size changes
    observerRef.current = new ResizeObserver(updateRect);
    observerRef.current.observe(element);

    return () => {
      window.removeEventListener('scroll', updateRect);
      window.removeEventListener('resize', updateRect);
      observerRef.current?.disconnect();
    };
  }, [state.active, state.element]);

  // Don't render if not active or no element to highlight
  if (!state.active || !rect) {
    return null;
  }

  const padding = 8;

  return (
    <div className="fixed inset-0 z-dropdown pointer-events-none">
      {/* Semi-transparent overlay with cutout */}
      <div
        className="absolute inset-0 bg-black/40 transition-opacity duration-300"
        style={{
          clipPath: `polygon(
            0% 0%,
            0% 100%,
            ${rect.left - padding}px 100%,
            ${rect.left - padding}px ${rect.top - padding}px,
            ${rect.left + rect.width + padding}px ${rect.top - padding}px,
            ${rect.left + rect.width + padding}px ${rect.top + rect.height + padding}px,
            ${rect.left - padding}px ${rect.top + rect.height + padding}px,
            ${rect.left - padding}px 100%,
            100% 100%,
            100% 0%
          )`,
        }}
        onClick={() => dismiss()}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Highlight ring */}
      <div
        className="absolute border-2 border-primary rounded-lg shadow-lg shadow-primary/30 animate-pulse"
        style={{
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        }}
      />

      {/* Tooltip */}
      {state.message && (
        <div
          className={cn(
            "absolute pointer-events-auto bg-popover text-popover-foreground border rounded-lg shadow-lg p-3 max-w-xs",
            "animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
          )}
          style={{
            left: Math.max(16, Math.min(rect.left, window.innerWidth - 280)),
            ...(tooltipPosition === 'bottom'
              ? { top: rect.top + rect.height + padding + 12 }
              : { bottom: window.innerHeight - rect.top + padding + 12 }
            ),
          }}
        >
          <div className="flex items-start gap-2">
            <p className="text-sm flex-1">{state.message}</p>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0 -mt-1 -mr-1"
              onClick={dismiss}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Click to dismiss hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-auto">
        <Button variant="ghost"
          onClick={dismiss}
          className="text-xs text-white/70 hover:text-white bg-black/50 px-3 py-1.5 rounded-full transition-colors !h-auto md:!px-3 md:!py-1.5 !gap-0 md:!text-xs hover:bg-black/50"
        >
          Click anywhere to dismiss
        </Button>
      </div>
    </div>
  );
}
